import { useEffect, useRef, useState } from "react"
import { BookOpen, Check, Download, FileText, LayoutTemplate, RotateCcw } from "lucide-react"

import { PanelCard, PanelCardHead, PanelCardBody } from "./PanelCard"
import PanelEmpty from "./PanelEmpty"
import { Avatar } from "../Avatar"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

function isPdf(file) {
  return file?.fileType === "application/pdf" || /\.pdf$/i.test(file?.fileName || "")
}

function isEpub(file) {
  return file?.fileType === "application/epub+zip" || /\.epub$/i.test(file?.fileName || "")
}

function formatSize(bytes) {
  if (!bytes) return null
  const mb = bytes / (1024 * 1024)
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`
}

// Renders the real paperback PDF with PDF.js (page → canvas). Lazy-loads the
// library + worker only when this view mounts, so the heavy bundle never loads
// on other tabs (docs/DECISIONS.md §Order detail).
function PdfView({ url }) {
  const canvasRef = useRef(null)
  const [status, setStatus] = useState("loading")
  const [pages, setPages] = useState(null)

  useEffect(() => {
    let cancelled = false

    ;(async () => {
      try {
        const pdfjs = await import("pdfjs-dist")
        const { default: PdfWorker } = await import("pdfjs-dist/build/pdf.worker.min.mjs?worker")
        pdfjs.GlobalWorkerOptions.workerPort = new PdfWorker()

        const doc = await pdfjs.getDocument(url).promise
        if (cancelled) return
        setPages(doc.numPages)

        const page = await doc.getPage(1)
        const viewport = page.getViewport({ scale: 1.25 })
        const canvas = canvasRef.current
        if (!canvas || cancelled) return
        canvas.width = viewport.width
        canvas.height = viewport.height
        await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise
        if (!cancelled) setStatus("ready")
      } catch {
        if (!cancelled) setStatus("error")
      }
    })()

    return () => {
      cancelled = true
    }
  }, [url])

  return (
    <div className="flex flex-col items-center">
      {status === "error" ? (
        <p className="py-10 text-[13px] text-ink-3">Couldn&apos;t render this proof inline. Use Download to open it.</p>
      ) : (
        <>
          <canvas ref={canvasRef} className="max-w-full rounded-md border border-border shadow-[0_18px_44px_-22px_rgba(0,0,0,0.4)]" />
          <div className="mt-3 text-[11.5px] text-ink-3">
            {status === "loading" ? "Rendering proof…" : pages ? `Page 1 of ${pages}` : "Page 1"}
          </div>
        </>
      )}
    </div>
  )
}

// Renders the real eBook EPUB with epub.js (reflowable, paginated).
function EpubView({ url }) {
  const hostRef = useRef(null)
  const [status, setStatus] = useState("loading")

  useEffect(() => {
    let cancelled = false
    let rendition = null

    ;(async () => {
      try {
        const { default: ePub } = await import("epubjs")
        if (cancelled || !hostRef.current) return

        const book = ePub(url)
        rendition = book.renderTo(hostRef.current, { width: "100%", height: 460, spread: "none" })
        await rendition.display()
        if (!cancelled) setStatus("ready")
      } catch {
        if (!cancelled) setStatus("error")
      }
    })()

    return () => {
      cancelled = true
      try {
        rendition?.destroy()
      } catch {
        /* noop */
      }
    }
  }, [url])

  if (status === "error") {
    return <p className="py-10 text-center text-[13px] text-ink-3">Couldn&apos;t render this eBook inline. Use Download to open it.</p>
  }

  return (
    <div className="mx-auto w-full max-w-[460px]">
      <div ref={hostRef} className="h-[460px] overflow-hidden rounded-2xl border border-border bg-[#FAF8F3] shadow-[0_18px_44px_-22px_rgba(0,0,0,0.4)]" />
      {status === "loading" && <div className="mt-3 text-center text-[11.5px] text-ink-3">Opening eBook…</div>}
    </div>
  )
}

// Formatted Proof tab — the real delivered paperback (PDF) and/or eBook (EPUB),
// rendered inline. A formatting order leads with this tab instead of the
// manuscript "Document" tab. Until the proof is delivered it shows the not-ready
// state. Mirrors order-proof.html.
export default function ProofPanel({ order, onOpenModal }) {
  const proof = order.deliverables?.proof
  const formatter = order.team?.[0]
  const canApprove = order.permissions?.approveFinal
  const canRevise = order.permissions?.requestRevision

  const pdf = isPdf(proof)
  // A collaborator's read-only view (AU-44): the empty states keep their
  // message but drop the control, which leads to the owner's chat or support.
  const readOnly = Boolean(order.viewer?.readOnly)
  const epub = isEpub(proof)

  const [view, setView] = useState(epub ? "ebook" : "paper")

  if (!proof) {
    return (
      <PanelEmpty
        Icon={LayoutTemplate}
        title="Formatted Proof"
        heading="Your proof is being prepared"
        body="Your formatter is turning your manuscript into a KDP-ready paperback and eBook. You'll be notified the moment your proof is ready to review."
        action={
          !readOnly && (
            <Button variant="outline" size="sm" className="gap-[7px]" onClick={() => onOpenModal?.("support")}>
              Message your formatter
            </Button>
          )
        }
      />
    )
  }

  const renderable = pdf || epub

  return (
    <PanelCard>
      <PanelCardHead Icon={LayoutTemplate} title="Formatted Proof">
        {formatter && (
          <span className="flex items-center gap-2 text-[12.5px] text-ink-3">
            Formatted by <Avatar initials={formatter.initials} name={formatter.name} size={22} />
            <b className="font-semibold text-foreground">{formatter.name}</b>
          </span>
        )}
      </PanelCardHead>

      <PanelCardBody className="bg-accent">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          {pdf && epub ? (
            <div className="inline-flex rounded-[10px] border border-border bg-secondary p-0.5">
              <button
                type="button"
                onClick={() => setView("paper")}
                className={cn("flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-[12.5px] font-semibold", view === "paper" ? "bg-card text-foreground shadow-sm" : "text-ink-3")}
              >
                <BookOpen className="size-[15px]" strokeWidth={1.9} /> Paperback
              </button>
              <button
                type="button"
                onClick={() => setView("ebook")}
                className={cn("flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-[12.5px] font-semibold", view === "ebook" ? "bg-card text-foreground shadow-sm" : "text-ink-3")}
              >
                <FileText className="size-[15px]" strokeWidth={1.9} /> eBook
              </button>
            </div>
          ) : (
            <div className="text-[12.5px] font-semibold text-ink-3">{pdf ? "Paperback (PDF)" : epub ? "eBook (EPUB)" : proof.fileName}</div>
          )}

          <Button asChild variant="outline" size="sm" className="gap-[7px]">
            <a href={proof.url} download={proof.fileName}>
              <Download className="size-4" strokeWidth={2} /> Download proof
            </a>
          </Button>
        </div>

        {/* The typeset stage pans sideways below 720 rather than clipping: a
            renderer that lays the proof out wider than a phone (a fixed-width
            spread) stays reachable, and the scroller keeps that width off the
            page. Never make it fit by shrinking the proof itself. */}
        <div className="rounded-2xl border border-border bg-secondary p-6 max-[720px]:overflow-x-auto max-[720px]:px-4 max-[720px]:py-5">
          {!renderable ? (
            <p className="py-8 text-center text-[13px] text-ink-3">
              Your proof ({proof.fileName}) is ready to download.
            </p>
          ) : view === "ebook" && epub ? (
            <EpubView url={proof.url} />
          ) : pdf ? (
            <PdfView url={proof.url} />
          ) : (
            <EpubView url={proof.url} />
          )}
        </div>

        {/* real file facts — no invented KDP specs */}
        <div className="mt-4 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-border bg-border min-[901px]:grid-cols-3">
          {[
            ["File", proof.fileName],
            ["Format", pdf ? "Paperback PDF" : epub ? "eBook EPUB" : (proof.fileType || "—")],
            ["Size", formatSize(proof.fileSize) || "—"],
          ].map(([k, v]) => (
            <div key={k} className="bg-card px-4 py-3">
              <div className="text-[10px] font-bold tracking-[0.05em] text-ink-3 uppercase">{k}</div>
              <div className="mt-1 truncate font-display text-[13.5px] font-bold" title={v}>{v}</div>
            </div>
          ))}
        </div>
      </PanelCardBody>

      {(canApprove || canRevise) && (
        <div className="flex flex-wrap items-center gap-4 border-t border-border bg-gradient-to-b from-card to-accent px-[22px] py-4 max-[720px]:gap-3 max-[720px]:px-4 max-[720px]:py-[14px]">
          <div className="max-[720px]:basis-full">
            <h4 className="font-display text-sm font-bold">Happy with the formatting?</h4>
            <p className="mt-0.5 text-[12px] text-ink-3">Approving delivers your print-ready paperback and eBook files.</p>
          </div>
          <div className="ml-auto flex gap-[9px] max-[720px]:w-full">
            {canRevise && (
              <Button variant="outline" className="gap-[7px] max-[720px]:flex-1" onClick={() => onOpenModal?.("request-revision")}>
                <RotateCcw className="size-4" strokeWidth={2} /> Request changes
              </Button>
            )}
            {canApprove && (
              <Button className="gap-[7px] font-bold max-[720px]:flex-1" onClick={() => onOpenModal?.("approve-final")}>
                <Check className="size-4" strokeWidth={2.2} /> Approve proof
              </Button>
            )}
          </div>
        </div>
      )}
    </PanelCard>
  )
}
